import { Link } from 'react-router-dom';

const collectionSteps = [
  {
    icon: 'ri-calendar-check-line',
    title: 'Schedule a Collection',
    description: 'Contact us to schedule a convenient date and time for our team to collect your documents. We come to your home, office, or any preferred location in Lusaka.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Prepare Your Documents',
    description: 'Gather all required documents including your passport, passport photos, bank statements, invitation letters, hotel bookings, and any other supporting documents needed for your visa application.',
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Our Team Collects',
    description: 'A Premium Visa Solutions representative arrives at your scheduled time to collect and verify your documents. Each document is logged and a receipt is provided.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Secure Handling',
    description: 'Your documents are transported securely to our office. We maintain a strict chain of custody and your passport and sensitive documents are stored in secure facilities.',
  },
  {
    icon: 'ri-government-line',
    title: 'Submission to Embassy',
    description: 'After document review and verification, we submit your complete application package to the relevant embassy or visa application centre.',
  },
  {
    icon: 'ri-truck-line',
    title: 'Return Delivery',
    description: 'Once your passport is ready, we collect it from the embassy and deliver it back to your preferred location. You receive notification at every step.',
  },
];

export default function DocumentCollection() {
  return (
    <div>
      <section className="relative h-[280px] md:h-[350px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20document%20handling%20concept%20with%20navy%20blue%20background%2C%20organized%20passports%20and%20travel%20documents%20neatly%20arranged%2C%20orange%20accent%20folders%2C%20clean%20corporate%20style%2C%20secure%20document%20management%20theme&width=1920&height=600&seq=doc-collection-visa&orientation=landscape"
          alt="Document Collection Service"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="Document Collection - Premium Visa Solutions"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/75 to-primary-950/65"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3">Document Collection</h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-[550px] mx-auto">
            We collect your documents from your preferred location and deliver them back after processing
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-14 mb-14">
            <div className="flex-1">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                Secure Document Collection and Delivery Service
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                At Premium Visa Solutions, we understand that your passport and supporting documents are among your most valuable possessions. That&apos;s why we offer a professional document collection and delivery service designed to give you complete peace of mind.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Our trained representatives come to your home, office, or any preferred location in Lusaka to collect your documents. Each document is carefully logged, and you receive a detailed receipt. Your documents are then transported securely to our office for processing.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                After your visa application is processed and your passport is ready, we collect it from the embassy or visa centre and deliver it back to you. You will never have to worry about document security or make unnecessary trips to our office.
              </p>
            </div>
            <div className="flex-1">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20hand%20holding%20a%20navy%20blue%20passport%20with%20world%20map%20in%20background%2C%20secure%20document%20handling%20concept%2C%20clean%20corporate%20office%20setting%2C%20warm%20lighting%2C%20orange%20accent%20details%2C%20trust%20and%20security%20theme&width=800&height=600&seq=doc-collection-secure&orientation=landscape"
                alt="Secure document handling"
                className="w-full rounded-lg object-cover object-top"
                title="Secure Document Collection - Premium Visa Solutions"
              />
            </div>
          </div>

          <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-10">
            How Our Document Collection Works
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {collectionSteps.map((step, idx) => (
              <div key={idx} className="bg-secondary-100/70 border border-secondary-200 rounded-lg p-5 md:p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-primary-500 text-background-50 rounded-lg flex-shrink-0">
                    <span className="text-xs font-bold">{idx + 1}</span>
                  </div>
                  <div className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-lg">
                    <i className={`${step.icon} text-accent-600 text-base`}></i>
                  </div>
                </div>
                <h4 className="font-heading font-semibold text-base text-foreground-900 mb-2">{step.title}</h4>
                <p className="text-sm text-foreground-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-primary-900">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
            Ready to Schedule a Document Collection?
          </h2>
          <p className="text-sm md:text-base text-background-50/70 max-w-[600px] mx-auto mb-8">
            Contact us to arrange a convenient time for our team to collect your documents, or use our Apply From Home service.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/apply-from-home" className="w-full sm:w-auto px-10 py-3.5 bg-accent-500 text-background-50 font-semibold rounded-md hover:bg-accent-600 transition-colors text-sm whitespace-nowrap cursor-pointer">
              Apply From Home
            </Link>
            <Link to="/contact-us" className="w-full sm:w-auto px-10 py-3.5 bg-background-50/10 text-background-50 font-semibold rounded-md border border-background-50/30 hover:bg-background-50/20 transition-colors text-sm whitespace-nowrap cursor-pointer">
              Contact Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}